/**
 * horizontal-loop.js
 * Optimized implementation of the horizontalLoop helper function
 */

/**
 * Creates a horizontally looping animation for a group of elements
 * @param {Array|NodeList|String} items - Elements to animate
 * @param {Object} config - Configuration options
 * @returns {Object} Timeline instance with added methods
 */
function horizontalLoop(items, config) {
  // Store original items reference and convert to array if needed
  const originalItems = items;
  items = gsap.utils.toArray(items);

  // Exit early if no items
  if (!items.length) {
    console.warn('No items found for horizontal loop');
    return null;
  }

  // Default configuration
  config = config || {};

  // Extract configuration properties
  const onChange = config.onChange;
  const center = config.center;
  const speed = config.speed || 1;
  const repeatValue = config.repeat !== undefined ? config.repeat : -1; // Default to -1 (infinite)
  const enableLoop = repeatValue !== 0; // If repeat is 0, disable looping
  const snap = config.snap === false ? v => v : gsap.utils.snap(config.snap || 1);
  const containerEl = center === true ? items[0].parentNode :
    gsap.utils.toArray(center)[0] || items[0].parentNode;

  // Create a timeline for the animation
  const tl = gsap.timeline({
    repeat: enableLoop ? repeatValue : 0, // Use the actual repeat value for looping, 0 for non-looping
    paused: config.paused,
    defaults: { ease: "none" },
    onUpdate: onChange && function () {
      const i = tl.closestIndex();
      if (lastIndex !== i) {
        lastIndex = i;
        onChange(items[i], i);
      }
    },
    // Only add onReverseComplete for looping timelines
    onReverseComplete: enableLoop ?
      () => tl.totalTime(tl.rawTime() + tl.duration() * 100) :
      null
  });

  // Initialize variables
  let lastIndex = 0;
  const length = items.length;
  const startX = items[0].offsetLeft;
  const times = [];
  const widths = [];
  const spaceBefore = [];
  const xPercents = [];
  const pixelsPerSecond = speed * 100;
  let totalWidth;
  let timeWrap;

  // Helper function to get total width
  const getTotalWidth = () => {
    return items[length - 1].offsetLeft +
      xPercents[length - 1] / 100 * widths[length - 1] -
      startX + spaceBefore[0] +
      items[length - 1].offsetWidth * gsap.getProperty(items[length - 1], "scaleX") +
      (parseFloat(config.paddingRight) || 0);
  };

  // Measure all elements and collect data
  const populateWidths = () => {
    let b1 = containerEl.getBoundingClientRect();
    let b2;

    items.forEach((el, i) => {
      // Measure dimensions
      widths[i] = parseFloat(gsap.getProperty(el, "width", "px"));

      // Get x positions as percentages for responsive animation
      xPercents[i] = snap(
        parseFloat(gsap.getProperty(el, "x", "px")) / widths[i] * 100 +
        gsap.getProperty(el, "xPercent")
      );

      // Measure spacing
      b2 = el.getBoundingClientRect();
      spaceBefore[i] = b2.left - (i ? b1.right : b1.left);
      b1 = b2;
    });

    // Set xPercent on all elements
    gsap.set(items, {
      xPercent: i => xPercents[i]
    });

    // Calculate total width
    totalWidth = getTotalWidth();
  };

  // Calculate offset times
  const populateOffsets = () => {
    var timeOffset = center ? tl.duration() * (containerEl.offsetWidth / 2) / totalWidth : 0;

    if (center) {
      times.forEach((t, i) => {
        times[i] = timeWrap(
          tl.labels["label" + i] +
          tl.duration() * widths[i] / 2 / totalWidth -
          timeOffset
        );
      });
    }
  };

  // Find closest index
  const getClosest = (values, value, wrap) => {
    let i = values.length;
    let closest = 1e10;
    let index = 0;
    let d;

    while (i--) {
      d = Math.abs(values[i] - value);
      if (d > wrap / 2) {
        d = wrap - d;
      }

      if (d < closest) {
        closest = d;
        index = i;
      }
    }

    return index;
  };

  // Build the animation timeline
  const populateTimeline = () => {
    let i, item, curX, distanceToStart, distanceToLoop;

    // Clear previous timeline
    tl.clear();

    if (enableLoop) {
      // LOOPING MODE - Original timeline with looping positioning
      for (i = 0; i < length; i++) {
        item = items[i];
        curX = xPercents[i] / 100 * widths[i];
        distanceToStart = item.offsetLeft + curX - startX + spaceBefore[0];
        distanceToLoop = distanceToStart + widths[i] * gsap.getProperty(item, "scaleX");

        // Create looping animation for each item
        tl.to(
          item,
          {
            xPercent: snap((curX - distanceToLoop) / widths[i] * 100),
            duration: distanceToLoop / pixelsPerSecond
          },
          0
        )
          .fromTo(
            item,
            {
              xPercent: snap((curX - distanceToLoop + totalWidth) / widths[i] * 100)
            },
            {
              xPercent: xPercents[i],
              duration: (curX - distanceToLoop + totalWidth - curX) / pixelsPerSecond,
              immediateRender: false
            },
            distanceToLoop / pixelsPerSecond
          )
          .add("label" + i, distanceToStart / pixelsPerSecond);

        times[i] = distanceToStart / pixelsPerSecond;
      }

      timeWrap = gsap.utils.wrap(0, tl.duration());
    } else {
      // NON-LOOPING MODE - Move all slides together maintaining their spacing
      // This creates a single coordinated movement of all slides
      // We need extra distance so the last slide can snap to the left
      const lastSlidePosition = items[length - 1].offsetLeft;
      const lastSlideWidth = widths[length - 1];
      const totalDistance = lastSlidePosition + lastSlideWidth; // Allow last slide to reach left edge

      // Create a single animation that moves all slides together
      for (i = 0; i < length; i++) {
        item = items[i];

        // Move each slide the same distance to maintain spacing/gaps
        tl.to(
          item,
          {
            x: -totalDistance, // Move all slides left by the extended distance
            duration: totalDistance / pixelsPerSecond,
            ease: "none"
          },
          0 // Start all animations at the same time
        );

        // Calculate when each slide becomes "active" based on container position
        const slidePosition = item.offsetLeft;
        times[i] = (slidePosition / totalDistance) * (totalDistance / pixelsPerSecond);
      }

      // Add labels for navigation
      for (i = 0; i < length; i++) {
        tl.add("label" + i, times[i]);
      }

      // Clamp time instead of wrapping
      timeWrap = (time) => Math.max(0, Math.min(time, tl.duration()));
    }
  };

  // Function to refresh the loop when needed
  const refresh = (deep) => {
    let progress = tl.progress();

    // Pause animation during refresh
    tl.progress(0, true);

    // Update measurements
    populateWidths();

    // Rebuild timeline if needed
    if (deep) {
      populateTimeline();
    }

    // Update offsets
    populateOffsets();

    // Restore progress
    if (deep && tl.draggable) {
      tl.time(times[curIndex], true);
    } else {
      tl.progress(progress, true);
    }

    return tl;
  };

  // Initialize with starting positions
  gsap.set(items, { x: 0 });
  populateWidths();
  populateTimeline();
  populateOffsets();

  // Add window resize handler with debounce
  let resizeTimeout;
  const resizeHandler = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => refresh(true), 200);
  };

  window.addEventListener("resize", resizeHandler);

  // Variables for navigation
  let curIndex = 0;
  let indexIsDirty = false;
  let proxy;

  // Function to navigate to specific index
  function toIndex(index, vars) {
    vars = vars || {};

    if (enableLoop) {
      // LOOPING MODE - Original behavior
      // Ensure shortest path navigation
      if (Math.abs(index - curIndex) > length / 2) {
        index += index > curIndex ? -length : length;
      }

      // Wrap index to valid range
      let newIndex = gsap.utils.wrap(0, length, index);
      let time = times[newIndex];

      // Adjust for timeline wrapping
      if (time > tl.time() !== index > curIndex && index !== curIndex) {
        time += tl.duration() * (index > curIndex ? 1 : -1);
      }

      // Handle time wrapping
      if (time < 0 || time > tl.duration()) {
        vars.modifiers = { time: timeWrap };
      }

      // Update current index
      curIndex = newIndex;
      vars.overwrite = true;

      // Kill any existing animations
      if (proxy) {
        gsap.killTweensOf(proxy);
      }

      // Navigate to time
      return vars.duration === 0 ?
        tl.time(timeWrap(time)) :
        tl.tweenTo(time, vars);
    } else {
      // NON-LOOPING MODE - Boundary-aware navigation
      // Clamp index to valid range (no wrapping)
      const clampedIndex = Math.max(0, Math.min(index, length - 1));

      // Don't navigate if we're already at the boundary and trying to go beyond
      if ((index < 0 && curIndex === 0) || (index >= length && curIndex === length - 1)) {
        return tl; // Return timeline without navigation
      }

      // Use the clamped index
      let newIndex = clampedIndex;
      let time = times[newIndex];

      // Update current index
      curIndex = newIndex;
      vars.overwrite = true;

      // Kill any existing animations
      if (proxy) {
        gsap.killTweensOf(proxy);
      }

      // Navigate to time (no wrapping in non-loop mode)
      return vars.duration === 0 ?
        tl.time(time) :
        tl.tweenTo(time, vars);
    }
  }

  // Add methods to the timeline
  tl.toIndex = (index, vars) => toIndex(index, vars);

  tl.closestIndex = setCurrent => {
    let index = getClosest(times, tl.time(), tl.duration());
    if (setCurrent) {
      curIndex = index;
      indexIsDirty = false;
    }

    return index;
  };

  tl.current = () => indexIsDirty ? tl.closestIndex(true) : curIndex;
  tl.next = vars => toIndex(tl.current() + 1, vars);
  tl.previous = vars => toIndex(tl.current() - 1, vars);
  tl.times = times;
  tl.progress(1, true).progress(0, true); // Pre-render for performance

  // Set direction
  if (config.reversed) {
    tl.vars.onReverseComplete();
    tl.reverse();
  }

  // Add draggable functionality if available
  if (config.draggable && typeof Draggable === "function") {
    proxy = document.createElement("div");
    let wrap = gsap.utils.wrap(0, 1);
    let ratio, startProgress, draggable, dragSnap, lastSnap, initChangeX;

    // Alignment function for dragging
    const align = () => {
      const newProgress = startProgress + (draggable.startX - draggable.x) * ratio;
      // Use wrap for looping, clamp for non-looping
      if (enableLoop) {
        tl.progress(wrap(newProgress));
      } else {
        // Clamp to boundaries for non-looping (0 = first slide, 1 = last slide)
        const clampedProgress = Math.max(0, Math.min(1, newProgress));
        tl.progress(clampedProgress);
      }
    };

    // Sync index after drag
    const syncIndex = () => tl.closestIndex(true);

    // Create draggable instance
    const draggableConfig = {
      trigger: items[0].parentNode,
      type: "x",
      onPressInit() {
        gsap.killTweensOf(tl);
        startProgress = tl.progress();
        refresh();
        ratio = 1 / totalWidth;
        initChangeX = (startProgress / -ratio) - this.x;
        gsap.set(proxy, { x: startProgress / -ratio });
      },
      onDrag: align,
      onThrowUpdate: align,
      overshootTolerance: 0,
      inertia: true,
      snap(value) {
        // Handle snap calculation
        if (Math.abs(startProgress / -ratio - this.x) < 10) {
          return lastSnap + initChangeX;
        }

        let time = -(value * ratio) * tl.duration();

        if (enableLoop) {
          // LOOPING MODE - Original snap behavior with wrapping
          let wrappedTime = timeWrap(time);
          let snapTime = times[getClosest(times, wrappedTime, tl.duration())];
          let dif = snapTime - wrappedTime;

          if (Math.abs(dif) > tl.duration() / 2) {
            dif += dif < 0 ? tl.duration() : -tl.duration();
          }

          lastSnap = (time + dif) / tl.duration() / -ratio;
          return lastSnap;
        } else {
          // NON-LOOPING MODE - Find closest without wrapping
          const clampedTime = Math.max(0, Math.min(time, tl.duration()));

          // Find closest slide without wrapping logic
          let closestIndex = 0;
          let minDistance = Math.abs(times[0] - clampedTime);

          for (let i = 1; i < times.length; i++) {
            const distance = Math.abs(times[i] - clampedTime);
            if (distance < minDistance) {
              minDistance = distance;
              closestIndex = i;
            }
          }

          let snapTime = times[closestIndex];
          lastSnap = snapTime / tl.duration() / -ratio;
          return lastSnap;
        }
      },
      onRelease() {
        syncIndex();
        draggable.isThrowing && (indexIsDirty = true);
      },
      onThrowComplete: syncIndex
    };

    // Add bounds for non-looping mode to prevent dragging beyond boundaries
    if (!enableLoop) {
      const firstSlidePos = 0 / -ratio;
      const lastSlidePos = tl.duration() / -ratio;
      draggableConfig.bounds = { left: lastSlidePos, right: firstSlidePos };
    }

    draggable = Draggable.create(proxy, draggableConfig)[0];

    tl.draggable = draggable;
  }

  // Initialize current index
  tl.closestIndex(true);
  lastIndex = curIndex;

  // Call onChange callback with initial values
  onChange && onChange(items[curIndex], curIndex);

  // Add destroy method for cleanup
  tl.destroy = () => {
    // Kill animations
    tl.kill();

    // Clear any pending resize timeout
    clearTimeout(resizeTimeout);

    // Remove event listeners
    window.removeEventListener("resize", resizeHandler);

    // Clean up draggable
    if (tl.draggable) {
      tl.draggable.kill();
    }

    // Reset elements
    gsap.set(items, { clearProps: "all" });

    // Remove from slider registry if using one
    if (window.sliderInstances && window.sliderInstances.length) {
      const index = window.sliderInstances.indexOf(tl);
      if (index !== -1) {
        window.sliderInstances.splice(index, 1);
      }
    }
  };

  // Add refresh method
  tl.refresh = refresh;

  // Return the enhanced timeline
  return tl;
}

export { horizontalLoop };